import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { getInitialMonth } from '../../utils/format';
import { LatePolicyInfoButton } from '../../components/LatePolicy';

const STATUS_COLORS = { present: 'pill-green', half_day: 'pill-orange', leave: 'pill-blue', absent: 'pill-red', holiday: 'pill-purple', week_off: 'pill-gray' };
const STATUS_LABELS = { present: 'Present', half_day: 'Half Day', leave: 'Leave', absent: 'Absent', holiday: 'Holiday', week_off: 'Week Off' };

export default function AttendancePage() {
  const { user } = useAuth();
  const isHR = user?.role === 'owner' || user?.role === 'admin';
  const qc = useQueryClient();
  const [month, setMonth] = useState(getInitialMonth);
  const [empFilter, setEmpFilter] = useState('');

  useEffect(() => { const h = e => setMonth(e.detail); window.addEventListener('monthChange', h); return () => window.removeEventListener('monthChange', h); }, []);
  function changeMonth(v) { setMonth(v); window.dispatchEvent(new CustomEvent('monthChange', { detail: v })); }
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminForm, setAdminForm] = useState({ employee_id: '', date: new Date().toISOString().slice(0, 10), check_in: '', check_out: '', status: 'present', note: '' });

  const { data: todayData, refetch: refetchToday } = useQuery({
    queryKey: ['att-today'],
    queryFn: () => api.get('/hr/attendance/today').then(r => r.data),
    enabled: !isHR,
  });

  const { data: attendance = [], refetch: refetchAtt } = useQuery({
    queryKey: ['hr-attendance', month, empFilter],
    queryFn: () => api.get(`/hr/attendance?month=${month}${empFilter ? `&employee_id=${empFilter}` : ''}`).then(r => r.data),
  });

  const { data: summary } = useQuery({
    queryKey: ['hr-att-summary', month, empFilter || 'me'],
    queryFn: () => api.get(`/hr/attendance/summary?month=${month}${empFilter ? `&employee_id=${empFilter}` : ''}`).then(r => r.data),
  });

  const { data: employees = [] } = useQuery({
    queryKey: ['hr-employees'],
    queryFn: () => api.get('/hr/employees').then(r => r.data),
    enabled: isHR,
  });

  const checkInMutation = useMutation({
    mutationFn: () => api.post('/hr/attendance/checkin'),
    onSuccess: () => { refetchToday(); toast.success('Checked in successfully'); },
    onError: err => toast.error(err.response?.data?.error || 'Failed'),
  });

  const checkOutMutation = useMutation({
    mutationFn: () => api.post('/hr/attendance/checkout'),
    onSuccess: () => { refetchToday(); toast.success('Checked out successfully'); },
    onError: err => toast.error(err.response?.data?.error || 'Failed'),
  });

  const adminMutation = useMutation({
    mutationFn: data => api.post('/hr/attendance/admin', data),
    onSuccess: () => {
      qc.invalidateQueries(['hr-attendance']);
      qc.invalidateQueries(['hr-att-summary']);
      toast.success('Attendance saved');
      setShowAdminModal(false);
    },
    onError: err => toast.error(err.response?.data?.error || 'Failed'),
  });

  function openEdit(a) {
    const toLocal = ts => {
      if (!ts) return '';
      const d = new Date(ts);
      return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    };
    setAdminForm({
      employee_id: a.employee_id,
      date: a.date?.slice(0, 10) || '',
      check_in: toLocal(a.check_in),
      check_out: toLocal(a.check_out),
      status: a.status || 'present',
      note: a.note || '',
    });
    setShowAdminModal(true);
  }

  const att = todayData?.attendance;
  const emp = todayData?.employee;

  return (
    <div>
      <div className="section-head">
        <h2>Attendance {user?.role !== 'owner' && <LatePolicyInfoButton />}</h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input type="month" value={month} onChange={e => changeMonth(e.target.value)} />
          {isHR && (
            <>
              <select value={empFilter} onChange={e => setEmpFilter(e.target.value)} style={{ width: 180 }}>
                <option value="">All Employees</option>
                {employees.map(e => <option key={e.id} value={e.id}>{e.full_name}</option>)}
              </select>
              <button className="btn-primary" onClick={() => setShowAdminModal(true)}>+ Manual Entry</button>
            </>
          )}
        </div>
      </div>

      {/* Self check-in/out widget for team members */}
      {!isHR && (
        <div className="card" style={{ marginBottom: 16 }}>
          {!emp ? (
            <div style={{ color: 'var(--orange)', fontSize: 13 }}>
              Your account is not linked to an employee record. Contact HR to set up your attendance.
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{emp.full_name}</div>
                <div style={{ fontSize: 12, color: 'var(--text2)' }}>{emp.employee_code} · {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
              </div>
              {att ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span className={`pill ${STATUS_COLORS[att.status]}`}>{STATUS_LABELS[att.status]}</span>
                  {att.check_in && <span style={{ fontSize: 12, color: 'var(--text2)' }}>In: {new Date(att.check_in).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>}
                  {att.check_out && <span style={{ fontSize: 12, color: 'var(--text2)' }}>Out: {new Date(att.check_out).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>}
                  {att.total_hours && <span style={{ fontSize: 12, color: 'var(--green)', fontWeight: 600 }}>{att.total_hours}h</span>}
                  {!att.check_out && <button className="btn btn-sm" style={{ background: 'var(--red)', color: '#fff', borderColor: 'var(--red)' }} onClick={() => checkOutMutation.mutate()} disabled={checkOutMutation.isPending}>Check Out</button>}
                </div>
              ) : (
                <button className="btn-primary" onClick={() => checkInMutation.mutate()} disabled={checkInMutation.isPending}>
                  {checkInMutation.isPending ? 'Checking in…' : '✓ Check In'}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Summary row */}
      {summary && (
        <div className="kpi-grid" style={{ marginBottom: 16 }}>
          <div className="kpi"><div className="kpi-label">Present</div><div className="kpi-val" style={{ color: 'var(--green)' }}>{summary.present || 0}</div></div>
          <div className="kpi"><div className="kpi-label">Half Day</div><div className="kpi-val" style={{ color: 'var(--orange)' }}>{summary.half_day || 0}</div></div>
          <div className="kpi"><div className="kpi-label">On Leave</div><div className="kpi-val" style={{ color: 'var(--accent)' }}>{summary.on_leave || 0}</div></div>
          <div className="kpi"><div className="kpi-label">Absent</div><div className="kpi-val" style={{ color: 'var(--red)' }}>{summary.absent || 0}</div></div>
          <div className="kpi"><div className="kpi-label">Total Hours</div><div className="kpi-val">{Number(summary.total_hours || 0).toFixed(1)}h</div></div>
        </div>
      )}

      {/* Attendance table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                {isHR && <th>Employee</th>}
                <th>Status</th>
                <th>Check In</th>
                <th>Check Out</th>
                <th>Hours</th>
                {isHR && <th>Edited By</th>}
                <th>Note</th>
                {isHR && <th></th>}
              </tr>
            </thead>
            <tbody>
              {attendance.map(a => {
                const isSat = new Date(a.date).getDay() === 6;
                return (
                <tr key={a.id}>
                  <td style={{ fontWeight: 500 }}>
                    {new Date(a.date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                    {isSat && <span style={{ fontSize: 10, marginLeft: 5, color: 'var(--accent)', fontWeight: 700 }}>SAT</span>}
                  </td>
                  {isHR && <td style={{ fontWeight: 500 }}>{a.full_name}</td>}
                  <td><span className={`pill ${STATUS_COLORS[a.status] || 'pill-gray'}`}>{STATUS_LABELS[a.status] || a.status}</span></td>
                  <td style={{ color: 'var(--text2)', fontSize: 12 }}>{a.check_in ? new Date(a.check_in).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                  <td style={{ color: 'var(--text2)', fontSize: 12 }}>{a.check_out ? new Date(a.check_out).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                  <td style={{ fontWeight: 500 }}>{a.total_hours ? `${a.total_hours}h` : '—'}</td>
                  {isHR && <td style={{ fontSize: 11, color: a.edited_by_name ? 'var(--orange)' : 'var(--text3)' }}>{a.edited_by_name || '—'}</td>}
                  <td style={{ fontSize: 12, color: 'var(--text2)' }}>{a.note || '—'}</td>
                  {isHR && (
                    <td>
                      <button className="btn-sm" onClick={() => openEdit(a)}>Edit</button>
                    </td>
                  )}
                </tr>
                );
              })}
              {attendance.length === 0 && <tr><td colSpan={isHR ? 9 : 6} className="empty">No attendance records for this period</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Admin manual entry modal */}
      {showAdminModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-title">Manual Attendance Entry</div>
            <form onSubmit={e => { e.preventDefault(); adminMutation.mutate(adminForm); }}>
              <div className="field">
                <label>Employee *</label>
                <select value={adminForm.employee_id} onChange={e => setAdminForm(f => ({ ...f, employee_id: e.target.value }))} required>
                  <option value="">Select employee</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.full_name} ({e.employee_code})</option>)}
                </select>
              </div>
              <div className="form-grid">
                <div className="field">
                  <label>Date *</label>
                  <input type="date" value={adminForm.date} onChange={e => {
                    const newDate = e.target.value;
                    setAdminForm(f => ({
                      ...f,
                      date: newDate,
                      // Carry the new date into check-in/out, keeping whichever time was already picked
                      check_in: f.check_in ? `${newDate}T${f.check_in.slice(11)}` : f.check_in,
                      check_out: f.check_out ? `${newDate}T${f.check_out.slice(11)}` : f.check_out,
                    }));
                  }} required />
                </div>
                <div className="field">
                  <label>Status</label>
                  <select value={adminForm.status} onChange={e => setAdminForm(f => ({ ...f, status: e.target.value }))}>
                    {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label>Check In Time</label>
                  <input type="datetime-local" value={adminForm.check_in} onChange={e => setAdminForm(f => ({ ...f, check_in: e.target.value }))} />
                </div>
                <div className="field">
                  <label>Check Out Time</label>
                  <input type="datetime-local" value={adminForm.check_out} onChange={e => setAdminForm(f => ({ ...f, check_out: e.target.value }))} />
                </div>
              </div>
              <div className="field">
                <label>Note</label>
                <input value={adminForm.note} onChange={e => setAdminForm(f => ({ ...f, note: e.target.value }))} placeholder="Reason for manual entry" />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowAdminModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={adminMutation.isPending}>Save Attendance</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
